import { ReactElement, useMemo } from 'react';

import { useTicketsForAscProject } from '@neo.tax/asc/hooks';
import {
  AscGroupingTicketOrCollection,
  AscProjectTpTicketsViewSortFields,
  SortDirection,
  SortNulls,
  GroupedTicketsBase,
  NOISE_REASONING_PREFIX,
} from '@neo.tax/shared';

type GroupingSelection = {
  isGroupingSelected: boolean;
  selectedTicketIds: Set<string>;
};

export interface AscGroupedTicketsProps {
  ascProjectId: string;
  groupingTicketOrCollection: AscGroupingTicketOrCollection;
  groupingSelections: Record<string, GroupingSelection>;
  setGroupingSelections: React.Dispatch<React.SetStateAction<Record<string, GroupingSelection>>>;
  searchKeyword: string | null;
  isMoveTicketsDisabled: boolean;
  // Whether the drawer this list renders in is a noise (Miscellaneous/Ops) project. Drives the
  // per-ticket reasoning prefix, mirroring TicketGroupingItem's grouping-level behavior.
  isNoiseProject?: boolean;
  onTicketCountChange?: (groupingId: string, count: number) => void;
}

export const AscGroupedTickets = ({
  ascProjectId,
  groupingSelections,
  groupingTicketOrCollection,
  isMoveTicketsDisabled,
  isNoiseProject = false,
  onTicketCountChange,
  searchKeyword,
  setGroupingSelections,
}: AscGroupedTicketsProps): ReactElement => {
  const { hasNextPage, loadMore, loading, tickets } = useTicketsForAscProject({
    ascProjectId,
    searchKeyword,
    groupingTicketOrCollection,
    sorting: {
      direction: SortDirection.Desc,
      field: AscProjectTpTicketsViewSortFields.CompletedDatetime,
      nulls: SortNulls.NullsLast,
    },
  });

  // Prefix the per-ticket reasoning in noise drawers so it reads as the routing explanation
  // (mirrors TicketGroupingItem). Done here rather than in the shared row component so the
  // shared tickets-list stays noise-agnostic.
  const ticketsWithReasoningContext = useMemo(() => {
    if (!isNoiseProject || !tickets) {
      return tickets;
    }
    return tickets.map((ticket) =>
      ticket.matchingReasoning
        ? { ...ticket, matchingReasoning: `${NOISE_REASONING_PREFIX}${ticket.matchingReasoning}` }
        : ticket,
    );
  }, [isNoiseProject, tickets]);

  return (
    <GroupedTicketsBase
      groupingId={groupingTicketOrCollection.id}
      tickets={ticketsWithReasoningContext}
      hasNextPage={hasNextPage}
      loadMore={loadMore}
      loading={loading}
      groupingSelections={groupingSelections}
      setGroupingSelections={setGroupingSelections}
      isMoveTicketsDisabled={isMoveTicketsDisabled}
      onTicketCountChange={onTicketCountChange}
    />
  );
};
